<template>
  <div class="flex flex-col h-full w-full">
    <div
      class="relative group flex-grow"
      :class="uploadClasses"
    >
      <form ref="form">
        <input
          class="absolute opacity-0 inset-0 cursor-pointer w-full h-full"
          type="file"
          :title="uploaderMessage || $t('functional.uploaderMessage')"
          data-test="uploader"
          @change="handleAdd"
          @click="error = false"
          @drop="handleDrop"
          @dragenter="dragging = true"
          @dragleave="dragging = false"
          :multiple="multiple"
          :accept="acceptedFileTypes"
          data-hj-suppress
          tabindex="-1"
          ref="fileUploadField"
        />
      </form>

      <div
        class="h-full w-full border-dashed border-4 rounded-lg border-signableBrandColours-midGrey focus:border-signableBrandColours-signableBlue focus:outline-none group-hover:border-signableBrandColours-signableBlue group-active:border-signableBrandColours-midGrey p-xs cursor-pointer flex flex-col"
        tabindex="0"
        :class="{'border-signableBrandColours-signableBlue text-signableBrandColours-signableBlue': dragging}"
        role="button"
        @keyup.enter.self="fileUploadField.click()"
      >
        <div class="flex flex-grow flex-col justify-center">

          <div v-if="showProcessingMessage" class="flex flex-col items-center">
            <SpinLoader
              class="mb-base"
              size="medium"
            />
            <p class="text-14 text-signableBrandColours-darkGrey">{{ $t('library.filePicker.addingDocument') }}</p>
          </div>

          <div
            v-else
            class="text-center font-normal flex flex-col gap-sm text-signableBrandColours-darkGrey group-hover:text-signableBrandColours-signableBlue group-active:text-signableBrandColours-darkGrey text-14"
            :class="{'text-signableBrandColours-signableBlue': dragging}"
          >
            <CloudUploadIcon class="float my-0 mx-auto" />
            <span>
              {{ uploaderMessage || $t('functional.uploaderMessage') }}
            </span>
            <slot></slot>
          </div>
          <div
            v-if="showSuccessMessage"
            class="flex flex-row bottom-base left-base items-center absolute"
          >
            <SuccessIndicator/>
            <p class="text-14 text-signableBrandColours-darkGrey ml-base">{{ $t('library.filePicker.documentAdded') }}</p>
          </div>
        </div>
      </div>
    </div>
    <ValidationError
      class="mt-base"
      :hasError="error"
      :errorMessage="$t('library.filePicker.unsupportedFileType')"
      :errorAdvice="
        typesString &&
        $t('library.filePicker.fileTypeAdvice', {
          types: typesString,
        })
      "
    />
  </div>
</template>

<script>
// @ts-check
import { defineComponent, onUnmounted, ref } from 'vue';

import ValidationError from '@/components/library/error/ValidationError';
import { CloudUploadIcon } from '@/components/library/icons';
import SpinLoader from '@/components/library/loaders/SpinLoader.vue';
import SuccessIndicator from '@/components/library/indicators/SuccessIndicator.vue';

/** @typedef {({ target: HTMLInputElement} & Event)} InputFileEvent */

export default defineComponent({
  components: {
    SuccessIndicator,
    SpinLoader,
    CloudUploadIcon,
    ValidationError,
  },
  props: {
    multiple: {
      type: Boolean,
      default: true,
    },
    acceptedFileTypes: {
      type: String,
      required: false,
    },
    acceptedFileTypesString: {
      type: String,
      required: false,
    },
    uploaderMessage: {
      type: String,
      required: false,
    },
    uploadClasses: {
      type: String,
      required: false,
      default: '',
    },
  },
  setup(props, { emit }) {
    /** @type {import('vue').Ref<HTMLFormElement|undefined>} */
    const form = ref();
    const error = ref(false);
    const dragging = ref(false);
    /** @type {import('vue').Ref<number|undefined>} */
    const errorTimeout = ref();

    const showProcessingMessage = ref(false);
    const showSuccessMessage = ref(false);
    const fileUploadField = ref();

    const typesString =
      props.acceptedFileTypesString ||
      props.acceptedFileTypes
        ?.split(',')
        .map(type => type.replace('.', ''))
        .join(', ');

    /**
     * Handles the event when a file is added.
     *
     * @param {InputFileEvent} event - The event object representing the file add event.
     */
    function handleAdd(event) {
      const { files } = event.target;

      // extract list of file extension types from submitted files
      const extensionList = [];
      Array.from(files || []).forEach((file) => {
        extensionList.push(`.${file.name.split('.').pop()}`.toLowerCase());
      });

      // create comparison list for allowed file extension types
      const allowedExtensionList = props.acceptedFileTypes?.split(',') || [];

      // if no acceptedFileTypes then all file types are allowed
      // otherwise compare the two file extension lists
      if (
        !allowedExtensionList.length ||
        extensionList.every(extension =>
          allowedExtensionList.includes(extension))
      ) {
        error.value = false;
        showProcessingMessage.value = true;

        setTimeout(() => {
          emit('add', files);
          showProcessingMessage.value = false;
          showSuccessMessage.value = true;
          form.value?.reset();

          setTimeout(() => {
            showSuccessMessage.value = false;
          }, 2000);
        }, 1000);
      } else {
        emit('invalidFiles');
        error.value = true;
        window.clearTimeout(errorTimeout.value);
        errorTimeout.value = window.setTimeout(() => {
          error.value = false;
        }, 10000);

        form.value?.reset();
      }
    }

    function handleDrop() {
      error.value = false;
      dragging.value = false;
    }

    onUnmounted(() => window.clearTimeout(errorTimeout.value));

    return {
      form,
      error,
      handleAdd,
      handleDrop,
      typesString,
      showProcessingMessage,
      showSuccessMessage,
      dragging,
      fileUploadField,
    };
  },
});
</script>
